import {useFormSubmission} from '../../contexts/form-submission-context';
import {FormFieldRenderer} from './form-field-renderer';
import {Alert, AlertDescription, AlertTitle, cn, useLanguage} from '@shondalai/ui-commons';
import {AlertTriangle} from 'lucide-react';
import {DraftStatus} from './draft-status';

export function FormCanvas() {
  const { form, pages, currentPage, errors, isDraftLoading, isDraftSaving } = useFormSubmission();
  const { t } = useLanguage();

  if (!form || !pages[currentPage]) {
    console.log('FormCanvas: No form or page data');
    return null;
  }

  const page = pages[currentPage];
  const pageErrors = Object.values(errors).filter(error =>
    page.fields.some(field => field.id === error.fieldId)
  );

  return (
    <div className="relative">
      {/* Draft Loading/Saving Status - positioned absolute at top of form container */}
      <DraftStatus isDraftLoading={isDraftLoading} isDraftSaving={isDraftSaving} />

      <div className="space-y-5">
        {/* Page Title & Description (for multi-page forms) */}
        {pages.length > 1 && (
          <div className="mb-5 space-y-1.5">
            <h2
              className="text-xl font-medium"
              style={{ color: 'var(--ef-primary, #0F172A)' }}
            >
              {page.title}
            </h2>
            {page.description && (
              <p
                className="text-sm font-light leading-relaxed"
                style={{ color: 'var(--ef-textSecondary, #64748B)' }}
              >
                {page.description}
              </p>
            )}
          </div>
        )}

      {/* Page-level Errors */}
      {pageErrors.length > 0 && (
        <Alert
          id="form-errors-alert"
          variant="destructive"
          className="mb-5 border-destructive/20 bg-destructive/5 scroll-mt-4"
          data-error="true"
        >
          <AlertTriangle className="h-4 w-4" strokeWidth={1.5} />
          <AlertTitle className="text-sm font-medium">{t('COM_EASYFORMS_PLEASE_CORRECT_THE_FOLLOWING', 'Please correct the following:')}</AlertTitle>
          <AlertDescription>
            <ul className="list-none space-y-1 mt-2">
              {pageErrors.map(error => (
                <li key={error.fieldId} className="text-xs flex items-start gap-2">
                  <span className="text-destructive mt-0.5">{t('COM_EASYFORMS_BULLET', '•')}</span>
                  <span>{error.message}</span>
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Render Sections and Fields */}
      {pages.length === 1 ? (
        // Single page - render all sections
        form.sections.map((section, sectionIndex) => (
          <div key={section.id} className={cn(
            "space-y-4",
            sectionIndex > 0 && "pt-6"
          )}>
            {/* Section Header */}
            {(section.title || section.description) && (
              <div
                className="space-y-1.5 pb-3"
                style={section.title ? {
                  borderBottom: '1px solid var(--ef-border, #E2E8F0)',
                  borderBottomColor: 'var(--ef-border, #E2E8F0)',
                  opacity: section.title ? undefined : 0.2
                } : undefined}
              >
                {section.title && (
                  <h3
                    className="text-lg font-medium"
                    style={{ color: 'var(--ef-primary, #0F172A)' }}
                  >
                    {section.title}
                  </h3>
                )}
                {section.description && (
                  <p
                    className="text-xs font-light leading-relaxed"
                    style={{ color: 'var(--ef-textSecondary, #64748B)' }}
                  >
                    {section.description}
                  </p>
                )}
              </div>
            )}

            {/* Section Rows */}
            {section.rows.map(row => (
              <div
                key={row.id}
                className={cn(
                  "grid gap-3 md:gap-4"
                )}
                style={{
                  gridTemplateColumns: `repeat(${row.columns || 12}, minmax(0, 1fr))`
                }}
              >
                {row.fields.map(field => {
                  // Check if field should be visible
                  if (!page.fields.find(f => f.id === field.id)) {
                    return null;
                  }

                  return (
                    <div
                      key={field.id}
                      className={cn("transition-all duration-200")}
                      style={{
                        gridColumn: `span ${field.config.gridColumnSpan || 12} / span ${field.config.gridColumnSpan || 12}`
                      }}
                    >
                      <FormFieldRenderer field={field} />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ))
      ) : (
        // Multi-page - render current section only
        (() => {
          const section = form.sections.find(s => s.id === page.sectionId);
          if (!section) return null;

          return (
            <div className="space-y-4">
              {section.rows.map(row => (
                <div
                  key={row.id}
                  className={cn(
                    "grid gap-3 md:gap-4"
                  )}
                  style={{
                    gridTemplateColumns: `repeat(${row.columns || 12}, minmax(0, 1fr))`
                  }}
                >
                  {row.fields.map(field => {
                    // Check if field should be visible
                    if (!page.fields.find(f => f.id === field.id)) {
                      return null;
                    }

                    return (
                      <div
                        key={field.id}
                        className={cn("transition-all duration-200")}
                        style={{
                          gridColumn: `span ${field.config.gridColumnSpan || 12} / span ${field.config.gridColumnSpan || 12}`
                        }}
                      >
                        <FormFieldRenderer field={field} />
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          );
        })()
      )}
      </div>
    </div>
  );
}
